/* global Word, console */
// Add Datadog import
import { datadogRum } from '@datadog/browser-rum'
import * as Sentry from '@sentry/react'
// @ts-expect-error the types from this library don't include the diff_wordMode function,
// which is only added in the fork. As far as I can tell, there's no way to reconcile the two
// if @types/diff-match-patch is installed.
import DiffMatchPatch from 'diff-match-patch'
import 'diff-match-patch-line-and-word'

import { subsetRange } from './select-utilities'
// import globally to enhance the class.
import { stripCarriageReturn, zip } from './utilities'

type Patch = {
  diffs: [number, string][]
  start1: number
  start2: number
  length1: number
  length2: number
}

type PatchDiff = {
  changeType: 'INSERT' | 'DELETE'
  text: string
  index: number
}

type WordPatch = {
  old: string
  new: string
  start: number
  changeType: 'INSERT' | 'DELETE' | 'EDIT'
}

export function transformSelection(
  trackedSelection: Word.Range,
  editedText: string,
  config: { onlyWarnOnValidationFailure?: boolean } = {
    onlyWarnOnValidationFailure: true,
  }
): Promise<void> {
  /**
   * Transforms the selected text in the document by applying a diff algorithm to show minimal changes.
   * This function ensures that only the necessary modifications are made to the text, reducing the amount of change tracking.
   *
   * @param {Word.Range} trackedSelection - The selected text range in the Word document.
   * @param {string} editedText - The new edited text to be applied to the selection.
   * @param {Object} config - Configuration options.
   * @param {boolean} config.onlyWarnOnValidationFailure - If true, it logs a warning instead of throwing an error on validation failure.
   * @returns {Promise<void>} A promise that resolves when the transformation is complete.
   *
   * Note: Future enhancements can include passing in style properties and other attributes.
   */

  // Start the overall function vital
  const transformRef = datadogRum.startDurationVital('transformSelection')

  return Word.run(trackedSelection, async function (context) {
    trackedSelection.load('text')
    context.document.changeTrackingMode = 'TrackAll'

    await context.sync()
    try {
      if (!trackedSelection.text) {
        throw new Error('No text found in tracked selection')
      }
      const originalText = stripCarriageReturn(trackedSelection.text)
      const newTextDirty = editedText
      const newText = stripCarriageReturn(newTextDirty)

      // Measure patch generation
      const patchGenRef = datadogRum.startDurationVital('patchGeneration')
      const patches = getPatches(originalText, newText)
      datadogRum.stopDurationVital(patchGenRef, {
        context: {
          textLength: originalText.length,
          newTextLength: newText.length,
          patchCount: patches.length,
        },
      })

      // For each patch, subset the trackedSelection by range
      const patchRangeRef = datadogRum.startDurationVital('patchRangeCreation')
      const patchRanges: Word.Range[] = []
      for (const patch of patches) {
        patchRanges.push(
          await subsetRange(
            trackedSelection,
            patch.start,
            patch.start + Math.max(1, patch.old.length)
          )
        )
      }
      datadogRum.stopDurationVital(patchRangeRef)

      // Throw error if any issue with the patches
      validatePatchesAndRanges(
        patches,
        patchRanges,
        originalText,
        newText,
        config
      )

      // For each patchRange, replace the text with the patch new text, or insert the patch new text at the start of the range
      const applyRef = datadogRum.startDurationVital('patchApplication')
      await Promise.all(
        zip(patches, patchRanges).map(([patch, patchRange]) => {
          if (patch.changeType == 'INSERT') {
            patchRange.insertText(patch.new, 'Before')
          } else {
            patchRange.insertText(patch.new, 'Replace')
          }
        })
      )
      datadogRum.stopDurationVital(applyRef)

      await context.sync()

      // Stop the overall function vital with success
      datadogRum.stopDurationVital(transformRef, {
        context: {
          success: true,
          textLength: originalText.length,
          newTextLength: newText.length,
          patchCount: patches.length,
        },
      })
    } catch (error) {
      Sentry.captureException(error)
      console.warn(
        'Minimal diff calculation failed. Fallback to replace all text.',
        error
      )

      // Fallback: Replace all old text with new text if minimal diff calculation fails
      const fallbackRef = datadogRum.startDurationVital('fallbackReplace')
      trackedSelection.insertText(editedText, 'Replace')
      await context.sync()
      datadogRum.stopDurationVital(fallbackRef)

      // Stop the overall function vital with error
      datadogRum.stopDurationVital(transformRef, {
        context: {
          success: false,
          error: error instanceof Error ? error.message : String(error),
        },
      })
    }
  })
}

export function getPatches(original: string, changed: string): WordPatch[] {
  /**
   * Generates the patches (differences) between the original and changed text.
   * Each patch includes a start index, end index, old text, and new text.
   * All indexes are 0-indexed and refer to the original text.
   *
   * @param {string} original - The original text.
   * @param {string} changed - The new edited text.
   * @returns {WordPatch[]} An array of patches representing the differences between the original and changed text.
   */

  const patchlib = new DiffMatchPatch()
  const diffs = patchlib.diff_wordMode(original, changed)
  patchlib.diff_cleanupSemantic(diffs)
  const patches = patchlib.patch_make(original, diffs)

  const wordPatches: WordPatch[] = patches.flatMap((patch: Patch) => {
    const previousPatches = patches.filter(
      (p: Patch) => p.start1 < patch.start1
    )
    const adjustment = previousPatches
      .map((p: Patch) => {
        const oldLength = p.diffs
          .filter((d) => d[0] == -1)
          .map((d) => d[1].length)
          .reduce((a, b) => a + b, 0)
        const newLength = p.diffs
          .filter((d) => d[0] == 1)
          .map((d) => d[1].length)
          .reduce((a, b) => a + b, 0)
        return oldLength - newLength
      })
      .reduce((a: number, b: number) => a + b, 0)
    let accumIndex = patch.start1 + adjustment

    // AHH patch.start1 is relative to the changed text not the original text... but we need the old index
    // so all previous diffs need to be summed up and accounted for
    let patchDiffGroup: PatchDiff[] = []
    const patchDiffs: PatchDiff[][] = []
    for (const diff of patch.diffs) {
      if (diff[0] == 1 || diff[0] == -1) {
        patchDiffGroup.push({
          changeType: diff[0] == 1 ? 'INSERT' : 'DELETE',
          index: accumIndex,
          text: diff[1],
        })
      } else if (diff[0] == 0 && patchDiffGroup.length > 0) {
        patchDiffs.push(patchDiffGroup)
        patchDiffGroup = []
      }

      if (diff[0] == 0 || diff[0] == -1) {
        accumIndex += diff[1].length
      }
    }

    if (patchDiffGroup.length > 0) {
      patchDiffs.push(patchDiffGroup)
    }

    const wordPatches = patchDiffs.map((group: PatchDiff[]) => {
      if (group.length == 1) {
        return {
          old: group[0].changeType == 'DELETE' ? group[0].text : '',
          new: group[0].changeType == 'INSERT' ? group[0].text : '',
          start: group[0].index,
          changeType: group[0].changeType,
        }
      } else if (group.length == 2) {
        return {
          old: group[0].text,
          new: group[1].text,
          start: group[0].index,
          changeType: 'EDIT',
        }
      } else {
        throw new Error(
          'Patch group length is invalid. It should be either 1 or 2.'
        )
      }
    })
    return wordPatches
  })
  return wordPatches
}

function validatePatchesAndRanges(
  patches: WordPatch[],
  patchRanges: Word.Range[],
  original: string,
  changed: string,
  config: { onlyWarnOnValidationFailure?: boolean } = {
    onlyWarnOnValidationFailure: true,
  }
): void {
  /**
   * Validates the patches and patchRanges to ensure they correctly represent the changes from the original text to the changed text.
   * Throws an error if the validation fails.
   *
   * @param {WordPatch[]} patches - The patches representing the differences between the original and changed text.
   * @param {Word.Range[]} patchRanges - The ranges in the document corresponding to the patches.
   * @param {string} original - The original text.
   * @param {string} changed - The new edited text.
   * @param {Object} config - Configuration options.
   * @param {boolean} config.onlyWarnOnValidationFailure - If true, logs a warning instead of throwing an error on validation failure.
   */

  // Helper function to handle warnings and errors based on config
  function handleValidationFailure(message: string) {
    console.warn(message)
    if (!config.onlyWarnOnValidationFailure) {
      throw new Error(message)
    }
  }

  // Check for degenerate cases
  if (!patches || !patchRanges || !original || !changed) {
    handleValidationFailure(
      'Validation failed: One or more input parameters are null or undefined.'
    )
    return
  }

  if (
    patches.length === 0 &&
    patchRanges.length === 0 &&
    original === changed
  ) {
    return // No changes needed
  }

  if (patches.length !== patchRanges.length) {
    handleValidationFailure(
      'Validation failed: The number of patches does not match the number of patch ranges.'
    )
    return
  }

  // Ensure there exists at least one patch that has changes
  const hasChanges = patches.some((patch) => patch.old !== patch.new)
  if (!hasChanges) {
    handleValidationFailure(
      'Validation failed: No patches with actual changes found.'
    )
    return
  }

  let reconstructedText = original

  for (let i = 0; i < patches.length; i++) {
    const patch = patches[i]
    const start = patch.start
    const oldLength = patch.old.length
    const newText = patch.new

    // Ensure the patchRange text matches the expected original text
    const rangeText = original.substring(start, start + oldLength)
    if (rangeText !== patch.old) {
      handleValidationFailure(
        `Patch validation failed: Original text mismatch at range ${i}. Expected "${patch.old}", found "${rangeText}".`
      )
      continue
    }

    // Apply the patch to reconstruct the new text
    reconstructedText =
      reconstructedText.substring(0, start) +
      newText +
      reconstructedText.substring(start + oldLength)
  }

  // Ensure the final reconstructed text matches the expected changed text
  if (reconstructedText !== changed) {
    handleValidationFailure(
      'Patch validation failed: Reconstructed text does not match the changed text.'
    )
  }
}

export async function replaceSelection(
  newText: string,
  insertionRange: Word.Range
): Promise<void> {
  /**
   * Replaces the current document selection with the provided text.
   * This is a lightweight alternative to transformSelection when tracking changes isn't required.
   *
   * @param {string} newText - The text to replace the current selection with
   * @param {Word.Range} insertionRange - The range to replace
   * @returns {Promise<void>} A promise that resolves when the replacement is complete
   */
  return Word.run(insertionRange, async (context) => {
    context.document.changeTrackingMode = 'TrackAll'
    insertionRange.insertText(newText, 'Replace')
    insertionRange.select()
    await context.sync()
  })
}
